import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  Avatar,
  Typography,
} from "@material-tailwind/react";
import {
  EllipsisVerticalIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  BellAlertIcon,
  BellIcon,
  FlagIcon,
} from "@heroicons/react/24/solid";
import Swal from "sweetalert2";
import Toaster from "../../components/Toaster";
import { Notification } from '../../pages/notifications/notification';
import useHasPermission from "@/components/useHasPermission";
import LanguageSubMenu from "./LanguageSubMenu";
import { useTranslation } from "react-i18next";
import CountrySubMenu from "./CountrySubMenu";

export function UserProfileMenu({ isCollapsed = false }) {
  const _dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { user } = useSelector((state) => state.user);
  const { t } = useTranslation("sidebar");

  if (!user) {
    return null;
  }

  const logout = () => ({ type: 'user/logout' });
  const clearUser = () => ({ type: 'user/clear' });

  const handleLogout = () => {
    Swal.fire({
      title: t("logoutConfirmTitle"),
      text: t("logoutConfirmText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("logoutConfirmButton"),
      cancelButtonText: t("cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        const logoutPromise = Promise.resolve();
        logoutPromise.then(() => {
          Toaster.success(t("logoutSuccess"));
          localStorage.clear();
          navigate('/sign-in');
          setTimeout(() => {
            _dispatch(clearUser());
          }, 200);
        }).catch((error) => {
          console.error("Logout failed:", error);
          Toaster.error(t("logoutError"));
        });
      }
    });
  };

  return (
    <>
      <Menu placement="top-end">
        <MenuHandler>
          <div className={`border-t border-gray-200/60 cursor-pointer hover:bg-gray-100 transition-colors ${isCollapsed ? "p-3 flex justify-center" : "p-4"}`}>
            
            {/* Collapsed: show only avatar, centered */}
            {isCollapsed ? (
              <div className="relative">
                <Avatar
                  src={user?.profile_image || '/img/photo-gallery.png'}
                  alt={user?.first_name || 'User'}
                  className="h-9 w-9 rounded-full object-cover"
                />
                {user?.unread_notifications > 0 && (
                  <span className="absolute top-0 right-0 block h-2 w-2 bg-red-500 rounded-full ring-2 ring-white"></span>
                )}
              </div>
            ) : (
              /* Expanded: show avatar + name + bell, same as before */
              <div className="flex items-center gap-3">
                <Avatar
                  src={user?.profile_image || '/img/photo-gallery.png'}
                  alt={user?.first_name || 'User'}
                  className="h-10 w-10 rounded-full object-cover"
                />
                <div className="flex-grow min-w-0">
                  <p className="font-semibold text-gray-800 text-sm truncate">
                    {user?.first_name} {user?.last_name || ''}
                  </p>
                  <p className="text-gray-500 text-xs truncate">
                    {user?.roles[0]}
                    {user?.roles[0] !== "Administrator" && user?.designation && (
                      <>
                        {" ("}
                        {user.designation.charAt(0).toUpperCase() + user.designation.slice(1)}
                        {")"}
                      </>
                    )}
                  </p>
                </div>
                <div className="relative">
                  <BellAlertIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  {user?.unread_notifications > 0 && (
                    <span className="absolute top-0 right-0 block h-2 w-2 bg-red-500 rounded-full ring-2 ring-white"></span>
                  )}
                </div>
              </div>
            )}
          </div>
        </MenuHandler>

        {/* Menu dropdown — completely unchanged */}
        <MenuList className="w-64 p-1 font-sans">
          <div className="flex items-center gap-3 p-3 border-b border-blue-gray-50 bg-blue-gray-50/50 rounded-t-lg pointer-events-none">
            <Avatar
              src={user?.profile_image || '/img/photo-gallery.png'}
              alt={user?.first_name}
              size="lg"
              className="border-2 border-white"
            />
            <div className="min-w-0">
              <Typography variant="h6" className="text-blue-gray-900 truncate">
                {user?.first_name} {user?.last_name || ''}
              </Typography>
              <Typography variant="small" className="font-normal text-gray-600 truncate">
                {user?.email}
              </Typography>
            </div>
          </div>
          <MenuItem className="my-1">
            <Link to="/dashboard/profile" className="w-full">
              <div className="flex items-center gap-3 text-blue-gray-700">
                <UserCircleIcon className="h-5 w-5" />
                <Typography variant="small" className="font-semibold">
                   {t("myProfile")}
                </Typography>
              </div>
            </Link>
          </MenuItem>
          <hr className="my-1 border-blue-gray-50" />
          <MenuItem className="my-1" onClick={() => setOpen(true)}>
            <Link className="w-full">
              <div className="flex items-center gap-3 text-blue-gray-700">
                <div className="relative">
                  <BellAlertIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  {user?.unread_notifications > 0 && (
                    <span className="absolute top-0 right-0 block h-2 w-2 bg-red-500 rounded-full ring-2 ring-white"></span>
                  )}
                </div>
                <Typography variant="small" className="font-semibold">
                    {t("notifications")}
                </Typography>
              </div>
            </Link>
          </MenuItem>
          <hr className="my-1 border-blue-gray-50" />
          {(user && user?.designation === 'manager' && !user.roles.includes('Administrator') && useHasPermission('member.view')) && (
            <>
              <MenuItem className="my-1">
                <Link to="/dashboard/members" className="w-full">
                  <div className="flex items-center gap-3 text-blue-gray-700">
                    <UserGroupIcon className="h-5 w-5" />
                    <Typography variant="small" className="font-semibold">
                        {t("teamMembers")}
                    </Typography>
                  </div>
                </Link>
              </MenuItem>
              <hr className="my-1 border-blue-gray-50" />
            </>
          )}
          <LanguageSubMenu />
           <hr className="my-1 border-blue-gray-50" />

          <CountrySubMenu />
         
          <hr className="my-1 border-blue-gray-50" />
          <MenuItem
            onClick={handleLogout}
            className="my-1 hover:!bg-red-500/10 focus:!bg-red-500/10 active:!bg-red-500/10"
          >
            <div className="flex items-center gap-3 text-red-500">
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <Typography variant="small" className="font-semibold">
                  {t("logout")}
              </Typography>
            </div>
          </MenuItem>
        </MenuList>
      </Menu>

      {open && <Notification onClose={() => setOpen(false)} />}
    </>
  );
}